/**
 * Typed fetch wrapper for cart-bff calls.
 *
 * Centralises:
 *  - Base URL resolution (`env.bffBaseUrl`).
 *  - Auth header injection (`Token-Id`, optional `Authorization: Bearer`
 *    for the tokenize-save call, optional `Checkout-Token` for confirm).
 *  - JSON parsing + the standard `BffError` envelope mapping. Every BFF
 *    error response has the shape `{ type, message, code }` (see
 *    `packages/cart-bff/src/lib/http-errors.ts`) — `BffError` preserves
 *    those fields plus the HTTP status so callers can branch precisely.
 *
 * Page-level services (`checkoutApi.ts`, `paymentMethodsApi.ts`) build on
 * top of this; UI components never call `fetch` directly.
 */

import { env } from '../lib/env';
import type { BffErrorBody } from '../types/api';

export class BffError extends Error {
  readonly status: number;
  readonly type: string;
  readonly code: string;

  constructor(input: { status: number; body: BffErrorBody }) {
    super(input.body.message);
    this.name = 'BffError';
    this.status = input.status;
    this.type = input.body.type;
    this.code = input.body.code;
  }
}

/** Thrown when the network call itself fails (DNS, offline, CORS, …). */
export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

export interface RequestOptions {
  /** GEA Brillion `Token-Id` (JWT). Sent on every route except the bearer-only save call. */
  tokenId?: string;
  /** `Authorization: Bearer <tokenizeSessionToken>` — used by POST /payment-methods/save only. */
  bearerToken?: string;
  /** `Checkout-Token` — short-lived JWT from POST /payment-session, sent on POST /confirm. */
  checkoutToken?: string;
  /** Country code forwarded as `X-Country-Code` (cart-bff infers cart locale from this). */
  countryCode?: string;
  /** Optional query-string parameters appended to the request URL. */
  query?: Record<string, string | undefined>;
  /** JSON-serialisable body. */
  body?: unknown;
  /** Per-call abort support. */
  signal?: AbortSignal;
}

export interface HttpClient {
  request<TResponse>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    path: string,
    options?: RequestOptions,
  ): Promise<TResponse>;
}

type FetchFn = typeof fetch;

/**
 * Build a client bound to the supplied base URL + fetch impl. Production
 * code calls `createHttpClient()` with no args; tests pass a stub fetch.
 */
export function createHttpClient(input?: {
  baseUrl?: string;
  fetchFn?: FetchFn;
}): HttpClient {
  const baseUrl = input?.baseUrl ?? env.bffBaseUrl;
  const fetchFn = input?.fetchFn ?? fetch;

  return {
    async request<TResponse>(
      method: 'GET' | 'POST' | 'PUT' | 'DELETE',
      path: string,
      options: RequestOptions = {},
    ): Promise<TResponse> {
      const headers: Record<string, string> = {
        Accept: 'application/json',
      };
      // Local dev (REACT_APP_IS_PRODUCTION=false): the local cart-bff stack has
      // no GEA Brillion token to validate, so authenticate every call as a
      // fixed test customer via `x-customer-id` and drop the `Token-Id` header.
      // Deployed builds (default) keep the real `Token-Id` auth model.
      if (env.isProduction) {
        if (options.tokenId) headers['Token-Id'] = options.tokenId;
      } else {
        headers['x-customer-id'] = env.customerId;
      }
      if (options.bearerToken) headers.Authorization = `Bearer ${options.bearerToken}`;
      if (options.checkoutToken) headers['Checkout-Token'] = options.checkoutToken;
      if (options.countryCode) headers['X-Country-Code'] = options.countryCode;
      if (options.body !== undefined) headers['Content-Type'] = 'application/json';

      let response: Response;
      try {
        const init: RequestInit = {
          method,
          headers,
          // CRA in dev does NOT use cookies for cross-origin BFF calls;
          // explicit 'omit' avoids accidental session leakage.
          credentials: 'omit',
        };
        if (options.body !== undefined) {
          init.body = JSON.stringify(options.body);
        }
        // Always provide a `signal`. MSW v2's request handler reads
        // `request.signal.addEventListener(...)` and crashes under jsdom
        // when the underlying Request was created without one (Node's
        // fetch does not synthesise a default AbortSignal). If the caller
        // already passed a signal we honour it; otherwise we create a
        // throwaway controller so MSW always sees a real signal.
        init.signal = options.signal ?? new AbortController().signal;

        const url = new URL(`${baseUrl}${path}`);
        for (const [key, value] of Object.entries(options.query ?? {})) {
          if (value !== undefined) {
            url.searchParams.set(key, value);
          }
        }

        response = await fetchFn(url.toString(), init);
      } catch (error) {
        throw new NetworkError(
          error instanceof Error ? error.message : 'Network request failed',
        );
      }

      // 204 No Content (rarely used by cart-bff today but supported).
      if (response.status === 204) return undefined as TResponse;

      const parsed = await parseResponseBody(response);

      if (!response.ok) {
        if (isBffErrorBody(parsed)) {
          throw new BffError({ status: response.status, body: parsed });
        }
        throw new BffError({
          status: response.status,
          body: {
            type: 'unknown_error',
            message: `HTTP ${response.status}`,
            code: 'unknown_error',
          },
        });
      }

      return parsed as TResponse;
    },
  };
}

function safeParseJson(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

async function parseResponseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';

  // Prefer JSON parser for JSON responses. Under jsdom + MSW, `text()` may
  // occasionally resolve as empty for mocked JSON while `json()` still works.
  if (contentType.includes('application/json')) {
    try {
      return (await response.json()) as unknown;
    } catch {
      return undefined;
    }
  }

  const text = await response.text();
  return text.length > 0 ? safeParseJson(text) : undefined;
}

function isBffErrorBody(value: unknown): value is BffErrorBody {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.type === 'string' && typeof v.message === 'string' && typeof v.code === 'string'
  );
}
